import { siblingMark } from "@board/groupBoardVisits";
import { formatClockTime } from "@scribenote/brian-design-system";

import { joinDots, weightText } from "./examRoomFormat";
import type {
  TreatmentLine,
  TreatmentPatient,
  TreatmentStep,
} from "./treatmentBoardView";

const clock = (iso: string): string => formatClockTime(new Date(iso));

/** "Mirtazapine · 1 tablet · partial, not redosed" */
export const stepText = (step: TreatmentStep): string =>
  joinDots(
    step.text,
    step.amount,
    step.partial ? "partial, not redosed" : null,
  );

/**
 * "given 2:35 · Sam Chen · recorded by Brian · say the right amount if that's wrong",
 * or "from the plan 2:13 · consented" while nothing has happened.
 */
export const trailText = (step: TreatmentStep): string => {
  if (!step.progress || !step.progressAt)
    return joinDots(
      `from the plan ${clock(step.plannedAt)}`,
      step.consented ? "consented" : null,
    );
  return joinDots(
    `${step.progress} ${clock(step.progressAt)}`,
    step.progressBy,
    step.byBrian ? "recorded by Brian" : null,
    step.byBrian && step.progress === "given" && step.amount
      ? "say the right amount if that's wrong"
      : null,
  );
};

/** "with Tofu · Dr. Katie · Sam · SQ fluids · 3.64 kg"; `siblings` are the household's other patients on this board. */
export const patientMeta = (
  patient: TreatmentPatient,
  siblings: readonly TreatmentPatient[],
): string =>
  joinDots(
    siblingMark(siblings.map((sibling) => sibling.visit)),
    patient.visit.dvmName,
    patient.staffName,
    patient.reason ?? patient.visit.visitType,
    patient.weight && weightText(patient.weight),
  );

/** "Treatment done · back to Exam 3" */
export const doneText = (patient: TreatmentPatient): string =>
  joinDots(
    "Treatment done",
    patient.returnRoom && `back to ${patient.returnRoom}`,
  );

const EARLIER_MS = 15 * 60_000;

/** Two or more lines finished over 15 minutes ago fold into one summary row. */
export const foldEarlier = (
  lines: TreatmentLine[],
  now: number,
): { shown: TreatmentLine[]; earlier: TreatmentLine[] } => {
  const earlier = lines.filter(
    (line) =>
      line.state === "done" &&
      line.progressAt !== null &&
      now - Date.parse(line.progressAt) > EARLIER_MS,
  );
  if (earlier.length < 2) return { shown: lines, earlier: [] };
  return {
    shown: lines.filter((line) => !earlier.includes(line)),
    earlier,
  };
};

/** "3 orders filled earlier · SQ fluids · Emavert · Mirtazapine" */
export const earlierText = (earlier: TreatmentLine[]): string =>
  joinDots(
    `${earlier.length} orders filled earlier`,
    ...earlier.map((line) => line.text),
  );

/** First and last finish times of folded lines, which all have one. */
export const earlierSpan = (
  earlier: TreatmentLine[],
): { first: string; last: string } => {
  const times = earlier
    .map((line) => line.progressAt)
    .filter((at): at is string => at !== null)
    .sort();
  return { first: times[0], last: times[times.length - 1] };
};

/** "2:35 – 2:48 · all recorded by Brian" */
export const earlierTrail = (earlier: TreatmentLine[]): string => {
  const { first, last } = earlierSpan(earlier);
  return joinDots(
    `${clock(first)} – ${clock(last)}`,
    earlier.every((line) => line.byBrian) ? "all recorded by Brian" : null,
  );
};
